/**
 * P2PTransfersTable Component
 * 
 * Displays Point-to-Point transfers (ferries, inter-city, etc.).
 * Data comes directly from quotation.quote_line_items.p2p_transfers
 * 
 * With showBreakup=true:
 *   Columns: Transfer | Route | Mode | Type | Pax | Rate | GST | Amount | Status
 * 
 * With showBreakup=false:
 *   Columns: Transfer | Route | Status (Amount only in footer totals)
 * 
 * Footer: Total Included | Total Optional
 * 
 * Usage:
 *   <P2PTransfersTable
 *     transfers={quotation.quote_line_items?.p2p_transfers}
 *     totals={{
 *       subtotal: quotation.quote_line_items?.p2p_subtotal,
 *       gst: quotation.quote_line_items?.p2p_gst_total,
 *       total: quotation.quote_line_items?.p2p_total
 *     }}
 *     packageType={quotation.route_type}
 *     showBreakup={true}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { Ship } from 'lucide-react';
import { COMPONENT_VARIANTS, formatCurrency } from './displayConfig';

const P2PTransfersTable = ({ 
  transfers = [],
  totals = {},
  packageType = 'custom',
  showBreakup = true,
  variant = 'detail',
  showTitle = true,
  className = ''
}) => {
  if (!transfers || transfers.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;
  
  // Check if any transfer has GST (only relevant when showBreakup is true)
  const hasGst = showBreakup && transfers.some(t => (t.gst_amount || 0) > 0);
  
  // Determine if transfer is included or optional based on inclusion_type
  const getTransferStatus = (transfer) => {
    const inclusionType = transfer.inclusion_type || transfer.show_as || 'Mandatory';
    // Map Mandatory -> Included, Optional -> Optional
    return inclusionType === 'Optional' ? 'Optional' : 'Included';
  };
  
  // Separate included and optional transfers
  const includedTransfers = transfers.filter(t => getTransferStatus(t) === 'Included');
  const optionalTransfers = transfers.filter(t => getTransferStatus(t) === 'Optional');
  
  // Calculate totals for included and optional
  const includedTotal = includedTransfers.reduce((sum, t) => sum + (t.calculated_amount || t.total_amount || t.amount || 0), 0);
  const optionalTotal = optionalTransfers.reduce((sum, t) => sum + (t.calculated_amount || t.total_amount || t.amount || 0), 0);
  
  // Format pricing type for display
  const formatPricingType = (type) => {
    if (!type) return '-';
    const normalized = type.toUpperCase().replace(/[_-]/g, ' ');
    if (normalized.includes('PERSON')) return 'Per Person';
    if (normalized.includes('VEHICLE')) return 'Per Vehicle';
    return type;
  };
  
  // Get column count for colspan calculations
  const getColSpan = (forTotal = false) => {
    if (!showBreakup) {
      return forTotal ? 2 : 3; // Transfer | Route | Status (no Amount column in rows)
    }
    let cols = 6; // Transfer | Route | Mode | Type | Pax | Rate
    if (hasGst) cols += 1;
    cols += 1; // Amount
    return forTotal ? cols - 1 : cols + 1; // +1 for Status in full row
  };
  
  // Render a single transfer row with full breakup
  const renderFullRow = (transfer, idx, isOptional = false) => {
    const bgClass = isOptional 
      ? 'bg-[color-mix(in_srgb,var(--cyan)_5%,white)]' 
      : (idx % 2 === 0 ? 'bg-white' : (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50'));
    const borderClass = isOptional ? 'border-[color-mix(in_srgb,var(--navy)_10%,transparent)]' : '';
    
    const rate = transfer.rate || transfer.unit_price || 0;
    const gstAmount = transfer.gst_amount || 0;
    const gstPercentage = transfer.gst_slab || transfer.gst_percentage || (gstAmount > 0 ? 5 : 0);
    const totalAmount = transfer.calculated_amount || transfer.total_amount || transfer.amount || 0;
    
    return (
      <tr key={`${isOptional ? 'opt' : 'inc'}-${idx}`} className={bgClass}>
        <td className={`${styles.padding} border-t ${borderClass} font-medium`}>
          {transfer.transfer_name || transfer.name || transfer.description || 'Transfer'}
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-gray-600`}>
          {transfer.from_place && transfer.to_place 
            ? `${transfer.from_place} → ${transfer.to_place}`
            : transfer.route || '-'
          }
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-center text-xs`}>
          {transfer.transport_mode || transfer.mode || 'Road'}
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-center text-xs`}>
          {formatPricingType(transfer.pricing_type)}
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-center`}>
          {transfer.pax || transfer.quantity || 1}
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-right`}>
          {formatCurrency(rate)}
        </td>
        {hasGst && (
          <td className={`${styles.padding} border-t ${borderClass} text-right text-gray-500`}>
            {gstAmount > 0 ? `${gstPercentage}% (${formatCurrency(gstAmount)})` : '-'}
          </td>
        )}
        <td className={`${styles.padding} border-t ${borderClass} text-right font-medium`}>
          {formatCurrency(totalAmount)}
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-center`}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            isOptional 
              ? 'bg-[color-mix(in_srgb,var(--cyan)_15%,white)] text-[var(--navy)]' 
              : 'bg-[color-mix(in_srgb,var(--ok)_10%,white)] text-[var(--ok)]'
          }`}>
            {isOptional ? 'Optional' : 'Included'}
          </span>
        </td>
      </tr>
    );
  };
  
  // Render a simplified row (no breakup - just Transfer, Route, Status)
  const renderSimpleRow = (transfer, idx, isOptional = false) => {
    const bgClass = isOptional 
      ? 'bg-[color-mix(in_srgb,var(--cyan)_5%,white)]' 
      : (idx % 2 === 0 ? 'bg-white' : (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50'));
    const borderClass = isOptional ? 'border-[color-mix(in_srgb,var(--navy)_10%,transparent)]' : '';
    
    return (
      <tr key={`${isOptional ? 'opt' : 'inc'}-${idx}`} className={bgClass}>
        <td className={`${styles.padding} border-t ${borderClass} font-medium`}>
          {transfer.transfer_name || transfer.name || transfer.description || 'Transfer'}
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-gray-600`}>
          {transfer.from_place && transfer.to_place 
            ? `${transfer.from_place} → ${transfer.to_place}`
            : transfer.route || '-'
          }
        </td>
        <td className={`${styles.padding} border-t ${borderClass} text-center`}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            isOptional 
              ? 'bg-[color-mix(in_srgb,var(--cyan)_15%,white)] text-[var(--navy)]' 
              : 'bg-[color-mix(in_srgb,var(--ok)_10%,white)] text-[var(--ok)]'
          }`}>
            {isOptional ? 'Optional' : 'Included'}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="p2p-transfers"
      data-show-breakup={showBreakup}
      data-testid="p2p-transfers-table"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <Ship className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Point-to-Point Transfers
        </h3>
      )}
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className={`w-full ${styles.fontSize}`}>
          <thead>
            <tr className={`${styles.headerBg} ${styles.headerText}`}>
              <th className={`${styles.padding} text-left font-semibold`}>Transfer</th>
              <th className={`${styles.padding} text-left font-semibold`}>Route</th>
              {showBreakup && (
                <>
                  <th className={`${styles.padding} text-center font-semibold w-20`}>Mode</th>
                  <th className={`${styles.padding} text-center font-semibold w-24`}>Type</th>
                  <th className={`${styles.padding} text-center font-semibold w-14`}>Pax</th>
                  <th className={`${styles.padding} text-right font-semibold w-24`}>Rate</th>
                  {hasGst && (
                    <th className={`${styles.padding} text-right font-semibold w-28`}>GST</th>
                  )}
                  <th className={`${styles.padding} text-right font-semibold w-28`}>Amount</th>
                </>
              )}
              <th className={`${styles.padding} text-center font-semibold w-24`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {/* Included Transfers */}
            {includedTransfers.map((transfer, idx) => 
              showBreakup ? renderFullRow(transfer, idx, false) : renderSimpleRow(transfer, idx, false)
            )}
            
            {/* Included Subtotal */}
            {includedTransfers.length > 0 && (
              <tr className="bg-[color-mix(in_srgb,var(--navy)_5%,white)] border-t-2 border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
                <td colSpan={showBreakup ? (hasGst ? 7 : 6) : 2} className={`${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                  Total Included Transfers
                </td>
                <td className={`${styles.padding} text-right font-bold text-[var(--navy)]`}>
                  {formatCurrency(includedTotal)}
                </td>
                {showBreakup && <td className={`${styles.padding}`}></td>}
              </tr>
            )}
            
            {/* Optional Transfers */}
            {optionalTransfers.length > 0 && (
              <>
                <tr className="bg-[color-mix(in_srgb,var(--cyan)_10%,white)]">
                  <td colSpan={showBreakup ? (hasGst ? 9 : 8) : 3} className={`${styles.padding} font-medium text-[var(--navy)]`}>
                    Optional Transfers (Not included in package total)
                  </td>
                </tr>
                {optionalTransfers.map((transfer, idx) => 
                  showBreakup ? renderFullRow(transfer, idx, true) : renderSimpleRow(transfer, idx, true)
                )}
                <tr className="bg-[color-mix(in_srgb,var(--cyan)_10%,white)] border-t border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
                  <td colSpan={showBreakup ? (hasGst ? 7 : 6) : 2} className={`${styles.padding} text-right font-semibold text-[var(--navy)]`}>
                    Total Optional Transfers
                  </td>
                  <td className={`${styles.padding} text-right font-bold text-[var(--navy)]`}>
                    {formatCurrency(optionalTotal)}
                  </td>
                  {showBreakup && <td className={`${styles.padding}`}></td>}
                </tr>
              </>
            )}
          </tbody>
        </table>
        
        {/* Note */}
        {includedTransfers.length > 0 && (
          <div className="px-3 py-2 bg-[color-mix(in_srgb,var(--navy)_5%,white)] text-xs text-[var(--navy)] border-t border-gray-200">
            Note: Included transfers are part of the package total.
          </div>
        )}
      </div>
    </div>
  );
};

export default P2PTransfersTable;
